import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { 
  Users, Store, ShoppingCart, TrendingUp, Search, MoreVertical, 
  Shield, AlertCircle, Wallet, DollarSign, CreditCard, Package2,
  Headphones, Package, Truck, Settings, MessageCircle, Link2, ShieldCheck, Flag, FileText, BarChart3
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { OrderManagement } from "@/components/admin/OrderManagement";
import { PaymentManagement } from "@/components/admin/PaymentManagement";
import { TrackingCenter } from "@/components/admin/TrackingCenter";
import { UserAccounts } from "@/components/admin/UserAccounts";
import SellerApproval from "@/components/admin/SellerApproval";
import AdminApproval from "@/components/admin/AdminApproval";
import ProductManagement from "@/components/admin/ProductManagement";
import { SelfDeliveryManagement } from "@/components/seller/SelfDeliveryManagement";
import PlatformSettings from "@/components/admin/PlatformSettings";
import { LiveChatAdmin } from "@/components/admin/LiveChatAdmin";
import AffiliateManagement from "@/components/admin/AffiliateManagement";
import KYCReview from "@/components/admin/KYCReview";
import { DisputeManagement } from "@/components/admin/DisputeManagement";
import { PlatformMonitoring } from "@/components/admin/PlatformMonitoring";
import BlogManagement from "@/components/admin/BlogManagement";
import SiteAnalytics from "@/components/admin/SiteAnalytics";
import LeadsManagement from "@/components/admin/LeadsManagement";
export default function AdminDashboard() {
  const { toast } = useToast();
  const [stats, setStats] = useState([
    { label: "Total Users", value: "0", change: "+0%", icon: Users, color: "text-blue-600" },
    { label: "Active Sellers", value: "0", change: "+0%", icon: Store, color: "text-purple-600" },
    { label: "Total Orders", value: "0", change: "+0%", icon: ShoppingCart, color: "text-green-600" },
    { label: "Revenue", value: "₦0", change: "+0%", icon: TrendingUp, color: "text-orange-600" },
  ]);

  const [escrowStats, setEscrowStats] = useState({
    total: 0,
    paid: 0,
    pending: 0
  });

  const [sellers, setSellers] = useState<any[]>([]);
  const [buyers, setBuyers] = useState<any[]>([]);
  const [recentTransactions, setRecentTransactions] = useState<any[]>([]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      // Fetch user stats
      const { count: totalUsers } = await supabase
        .from('profiles')
        .select('*', { count: 'exact', head: true });

      // Fetch seller count using user_roles table
      const { data: sellerRoles, error: sellerError } = await supabase
        .from('user_roles')
        .select('user_id', { count: 'exact', head: true })
        .eq('role', 'seller');
      
      const activeSellers = sellerRoles?.length || 0;

      // Fetch order stats
      const { data: orders, count: totalOrders } = await supabase
        .from('orders')
        .select('total_amount, payment_status', { count: 'exact' });

      const revenue = orders?.reduce((sum, order) => {
        return order.payment_status === 'completed' ? sum + Number(order.total_amount) : sum;
      }, 0) || 0;

      // Fetch escrow stats
      const { data: escrowAccounts } = await supabase
        .from('escrow_accounts')
        .select('balance_available, balance_pending, balance_released');

      const escrowTotal = escrowAccounts?.reduce((sum, acc) => 
        sum + Number(acc.balance_available) + Number(acc.balance_pending), 0) || 0;
      const escrowPaid = escrowAccounts?.reduce((sum, acc) => 
        sum + Number(acc.balance_released), 0) || 0;
      const escrowPending = escrowAccounts?.reduce((sum, acc) => 
        sum + Number(acc.balance_pending), 0) || 0;

      setEscrowStats({
        total: escrowTotal,
        paid: escrowPaid,
        pending: escrowPending
      });

      // Update stats
      setStats([
        { label: "Total Users", value: String(totalUsers || 0), change: "+0%", icon: Users, color: "text-blue-600" },
        { label: "Active Sellers", value: String(activeSellers || 0), change: "+0%", icon: Store, color: "text-purple-600" },
        { label: "Total Orders", value: String(totalOrders || 0), change: "+0%", icon: ShoppingCart, color: "text-green-600" },
        { label: "Revenue", value: `₦${(revenue / 1000000).toFixed(1)}M`, change: "+0%", icon: TrendingUp, color: "text-orange-600" },
      ]);

      // Fetch sellers using user_roles to identify them
      const { data: sellerIds } = await supabase
        .from('user_roles')
        .select('user_id')
        .eq('role', 'seller')
        .limit(4);
      
      const sellerUserIds = sellerIds?.map(s => s.user_id) || [];
      
      const { data: sellersData } = await supabase
        .from('profiles')
        .select('*, products(count), orders(total_amount)')
        .in('id', sellerUserIds);

      if (sellersData) {
        setSellers(sellersData.map(seller => ({
          id: seller.id,
          name: seller.business_name || seller.full_name || 'N/A',
          email: seller.username || 'N/A',
          products: seller.product_count || 0,
          sales: `₦${((seller as any).orders?.reduce((sum: number, o: any) => sum + Number(o.total_amount), 0) || 0).toLocaleString()}`,
          status: seller.verified ? 'verified' : 'pending',
          joined: new Date(seller.created_at).toLocaleDateString()
        })));
      }

      // Fetch buyers using user_roles to identify them
      const { data: buyerIds } = await supabase
        .from('user_roles')
        .select('user_id')
        .eq('role', 'buyer')
        .limit(4);
      
      const buyerUserIds = buyerIds?.map(b => b.user_id) || [];
      
      const { data: buyersData } = await supabase
        .from('profiles')
        .select('*, orders(count, total_amount)')
        .in('id', buyerUserIds);

      if (buyersData) {
        setBuyers(buyersData.map(buyer => ({
          id: buyer.id,
          name: buyer.full_name || buyer.username || 'N/A',
          email: buyer.username || 'N/A',
          orders: (buyer as any).orders?.length || 0,
          spent: `₦${((buyer as any).orders?.reduce((sum: number, o: any) => sum + Number(o.total_amount), 0) || 0).toLocaleString()}`,
          joined: new Date(buyer.created_at).toLocaleDateString()
        })));
      }

      // Fetch recent transactions with separate profile queries
      const { data: transactionsData } = await supabase
        .from('payment_transactions')
        .select('*, orders(buyer_id, seller_id)')
        .order('created_at', { ascending: false })
        .limit(4);

      if (transactionsData) {
        // Fetch profiles separately for each transaction
        const transactionsWithProfiles = await Promise.all(
          transactionsData.map(async (transaction) => {
            let buyerName = 'N/A';
            let sellerName = 'N/A';
            
            if ((transaction as any).orders?.buyer_id) {
              const { data: buyer } = await supabase
                .from('profiles')
                .select('full_name')
                .eq('id', (transaction as any).orders.buyer_id)
                .single();
              buyerName = buyer?.full_name || 'N/A';
            }
            
            if ((transaction as any).orders?.seller_id) {
              const { data: seller } = await supabase
                .from('profiles')
                .select('business_name')
                .eq('id', (transaction as any).orders.seller_id)
                .single();
              sellerName = seller?.business_name || 'N/A';
            }
            
            return {
              id: transaction.reference.slice(0, 10).toUpperCase(),
              seller: sellerName,
              buyer: buyerName,
              amount: `₦${Number(transaction.amount).toLocaleString()}`,
              status: transaction.status,
              date: new Date(transaction.created_at).toLocaleDateString()
            };
          })
        );
        
        setRecentTransactions(transactionsWithProfiles);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-subtle">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
              <Shield className="h-6 w-6 sm:h-8 sm:w-8 text-primary" />
              Admin Dashboard
            </h1>
            <p className="text-sm sm:text-base text-muted-foreground mt-1">Manage and monitor your platform</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" className="text-sm sm:text-base">
              <AlertCircle className="h-3 w-3 sm:h-4 sm:w-4 mr-2" />
              <span className="hidden sm:inline">View Reports</span>
              <span className="sm:hidden">Reports</span>
            </Button>
            <Button variant="gradient" className="text-sm sm:text-base">Export Data</Button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {stats.map((stat) => (
            <Card key={stat.label}>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  {stat.label}
                </CardTitle>
                <stat.icon className={`h-5 w-5 ${stat.color}`} />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stat.value}</div>
                <p className="text-xs text-green-600 mt-1">{stat.change} from last month</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Escrow Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Total in Escrow</CardTitle>
              <Wallet className="h-5 w-5 text-primary" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">₦{escrowStats.total.toLocaleString()}</div>
              <p className="text-xs text-muted-foreground mt-1">Across all sellers</p>
            </CardContent>
          </Card>
          
          <Card className="border-green-500/20 bg-gradient-to-br from-green-500/5 to-transparent">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Paid to Sellers</CardTitle>
              <DollarSign className="h-5 w-5 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-green-600">₦{escrowStats.paid.toLocaleString()}</div>
              <p className="text-xs text-muted-foreground mt-1">Successfully released</p>
            </CardContent>
          </Card>
          
          <Card className="border-yellow-500/20 bg-gradient-to-br from-yellow-500/5 to-transparent">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Pending Release</CardTitle>
              <AlertCircle className="h-5 w-5 text-yellow-600" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-yellow-600">₦{escrowStats.pending.toLocaleString()}</div>
              <p className="text-xs text-muted-foreground mt-1">Awaiting confirmation</p>
            </CardContent>
          </Card>
        </div>

        {/* Platform Monitoring */}
        <div className="mb-8">
          <PlatformMonitoring />
        </div>

        {/* Main Content Tabs */}
        <Tabs defaultValue="approvals" className="space-y-6">
          <TabsList className="w-full overflow-x-auto flex justify-start no-scrollbar">
            <TabsTrigger value="approvals" className="text-xs sm:text-sm whitespace-nowrap flex-shrink-0">
              <Shield className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
              Approvals
            </TabsTrigger>
            <TabsTrigger value="orders" className="text-xs sm:text-sm whitespace-nowrap flex-shrink-0">Orders</TabsTrigger>
            <TabsTrigger value="analytics" className="text-xs sm:text-sm whitespace-nowrap flex-shrink-0">
              <BarChart3 className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
              Analytics
            </TabsTrigger>
            <TabsTrigger value="leads" className="text-xs sm:text-sm whitespace-nowrap flex-shrink-0">Leads</TabsTrigger>
            <TabsTrigger value="users" className="text-xs sm:text-sm whitespace-nowrap flex-shrink-0">
              <Users className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">User Management</span>
              <span className="sm:hidden">Users</span>
            </TabsTrigger>
            <TabsTrigger value="payments" className="text-xs sm:text-sm whitespace-nowrap flex-shrink-0">Payments</TabsTrigger>
            <TabsTrigger value="products" className="text-xs sm:text-sm whitespace-nowrap flex-shrink-0">
              <Package2 className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
              Products
            </TabsTrigger>
            <TabsTrigger value="affiliates" className="text-xs sm:text-sm whitespace-nowrap flex-shrink-0">
              <Link2 className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Affiliates</span>
              <span className="sm:hidden">Affiliates</span>
            </TabsTrigger>
            <TabsTrigger value="tickets" className="text-xs sm:text-sm whitespace-nowrap flex-shrink-0">
              <MessageCircle className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Live Chat</span>
              <span className="sm:hidden">Chat</span>
            </TabsTrigger>
            <TabsTrigger value="tracking" className="text-xs sm:text-sm whitespace-nowrap flex-shrink-0">Tracking</TabsTrigger>
            <TabsTrigger value="self-delivery" className="text-xs sm:text-sm whitespace-nowrap flex-shrink-0">
              <Truck className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Self-Delivery</span>
              <span className="sm:hidden">Delivery</span>
            </TabsTrigger>
            <TabsTrigger value="settings" className="text-xs sm:text-sm whitespace-nowrap flex-shrink-0">
              <Settings className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
              Settings
            </TabsTrigger>
            <TabsTrigger value="kyc" className="text-xs sm:text-sm whitespace-nowrap flex-shrink-0">
              <ShieldCheck className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
              KYC
            </TabsTrigger>
            <TabsTrigger value="disputes" className="text-xs sm:text-sm whitespace-nowrap flex-shrink-0">
              <Flag className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
              Disputes
            </TabsTrigger>
            <TabsTrigger value="blog" className="text-xs sm:text-sm whitespace-nowrap flex-shrink-0">
              <FileText className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
              Blog
            </TabsTrigger>
          </TabsList>

          {/* Approvals Tab */}
          <TabsContent value="approvals">
            <Tabs defaultValue="sellers" className="space-y-4">
              <TabsList>
                <TabsTrigger value="sellers">
                  <Store className="h-4 w-4 mr-2" />
                  Seller Approvals
                </TabsTrigger>
                <TabsTrigger value="admins">
                  <Shield className="h-4 w-4 mr-2" />
                  Admin Approvals
                </TabsTrigger>
              </TabsList>
              <TabsContent value="sellers">
                <SellerApproval />
              </TabsContent>
              <TabsContent value="admins">
                <AdminApproval />
              </TabsContent>
            </Tabs>
          </TabsContent>
          
          <TabsContent value="orders">
            <OrderManagement />
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics">
            <SiteAnalytics />
          </TabsContent>

          <TabsContent value="leads">
            <LeadsManagement />
          </TabsContent>

          {/* Payments Tab */}
          <TabsContent value="payments">
            <PaymentManagement />
          </TabsContent>

          {/* Products Tab */}
          <TabsContent value="products">
            <ProductManagement />
          </TabsContent>

          {/* Affiliates Tab */}
          <TabsContent value="affiliates">
            <AffiliateManagement />
          </TabsContent>
          
          {/* Live Chat Tab */}
          <TabsContent value="tickets">
            <LiveChatAdmin />
          </TabsContent>

          {/* Tracking Tab */}
          <TabsContent value="tracking">
            <TrackingCenter />
          </TabsContent>

          {/* Self-Delivery Tab */}
          <TabsContent value="self-delivery">
            <SelfDeliveryManagement isAdmin={true} />
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <UserAccounts />
          </TabsContent>

          {/* KYC Tab */}
          <TabsContent value="kyc">
            <KYCReview />
          </TabsContent>

          {/* Sellers Tab */}
          <TabsContent value="sellers">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle>Seller Management</CardTitle>
                    <CardDescription>Manage and verify seller accounts</CardDescription>
                  </div>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input placeholder="Search sellers..." className="pl-10 w-64" />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Business Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Products</TableHead>
                      <TableHead>Total Sales</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Joined</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sellers.map((seller) => (
                      <TableRow key={seller.id}>
                        <TableCell className="font-medium">{seller.name}</TableCell>
                        <TableCell>{seller.email}</TableCell>
                        <TableCell>{seller.products}</TableCell>
                        <TableCell>{seller.sales}</TableCell>
                        <TableCell>
                          <Badge variant={seller.status === "verified" ? "default" : "secondary"}>
                            {seller.status}
                          </Badge>
                        </TableCell>
                        <TableCell>{seller.joined}</TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem>View Details</DropdownMenuItem>
                              <DropdownMenuItem>Verify Account</DropdownMenuItem>
                              <DropdownMenuItem>Send Message</DropdownMenuItem>
                              <DropdownMenuItem className="text-destructive">Suspend</DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Buyers Tab */}
          <TabsContent value="buyers">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle>Buyer Management</CardTitle>
                    <CardDescription>Monitor buyer accounts and activity</CardDescription>
                  </div>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input placeholder="Search buyers..." className="pl-10 w-64" />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Total Orders</TableHead>
                      <TableHead>Total Spent</TableHead>
                      <TableHead>Joined</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {buyers.map((buyer) => (
                      <TableRow key={buyer.id}>
                        <TableCell className="font-medium">{buyer.name}</TableCell>
                        <TableCell>{buyer.email}</TableCell>
                        <TableCell>{buyer.orders}</TableCell>
                        <TableCell>{buyer.spent}</TableCell>
                        <TableCell>{buyer.joined}</TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem>View Profile</DropdownMenuItem>
                              <DropdownMenuItem>Order History</DropdownMenuItem>
                              <DropdownMenuItem>Send Notification</DropdownMenuItem>
                              <DropdownMenuItem className="text-destructive">Block User</DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Transactions Tab */}
          <TabsContent value="transactions">
            <Card>
              <CardHeader>
                <CardTitle>Recent Transactions</CardTitle>
                <CardDescription>Monitor platform transactions and payments</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Transaction ID</TableHead>
                      <TableHead>Seller</TableHead>
                      <TableHead>Buyer</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {recentTransactions.map((transaction) => (
                      <TableRow key={transaction.id}>
                        <TableCell className="font-mono text-sm">{transaction.id}</TableCell>
                        <TableCell>{transaction.seller}</TableCell>
                        <TableCell>{transaction.buyer}</TableCell>
                        <TableCell className="font-semibold">{transaction.amount}</TableCell>
                        <TableCell>
                          <Badge variant={
                            transaction.status === "completed" ? "default" :
                            transaction.status === "processing" ? "secondary" :
                            "outline"
                          }>
                            {transaction.status}
                          </Badge>
                        </TableCell>
                        <TableCell>{transaction.date}</TableCell>
                        <TableCell>
                          <Button variant="ghost" size="sm">View</Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <PlatformSettings />
          </TabsContent>

          {/* Disputes Tab */}
          <TabsContent value="disputes">
            <DisputeManagement />
          </TabsContent>

          {/* Blog Tab */}
          <TabsContent value="blog">
            <BlogManagement />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}